import { Button } from "@/components/ui/button";
import { ArrowRight, PlayCircle, FileText, Clock, Camera } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/ui/Logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { motion } from "framer-motion";

// Landing page components
import { SwipeHeroAnimation } from "@/components/landing/SwipeHeroAnimation";
import { DemoStrip } from "@/components/landing/DemoStrip";
import { LiveCounter } from "@/components/landing/LiveCounter";
import { PricingInline } from "@/components/landing/PricingInline";
import { TestimonialSingle } from "@/components/landing/TestimonialSingle";
import { AgentIntro } from "@/components/landing/AgentIntro";
import { ArcDivider } from "@/components/landing/ArcDivider";
import { Footer } from "@/components/layout/Footer";
import { MetaTags } from "@/components/seo/MetaTags";
import { JsonLd, softwareApplicationSchema, faqSchema } from "@/components/seo/JsonLd";

const Landing = () => {
  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <MetaTags />
      <JsonLd data={softwareApplicationSchema} />
      <JsonLd data={faqSchema} />
      {/* Navigation - Minimal, sticky with blur */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/30 glass-strong">
        <div className="container flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/">
            <Logo size="sm" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link 
              to="/pricing" 
              className="hidden sm:block text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </Link>
            <ThemeToggle />
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex">
                Sign In
              </Button>
            </Link>
            <Link to="/auth">
              <Button size="sm" variant="hero">
                Start Free
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - Full viewport */}
      <section className="relative min-h-screen flex items-center pt-16">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent -z-10" />
        
        <div className="container px-4 sm:px-6 py-12 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Hero copy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-foreground mb-6">
                Swipe.{" "}
                <span className="text-gradient-primary">Done.</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                Your entire operations workflow reduced to one gesture. 
                Tasks come to you. Right to complete. Left to skip. That's it.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
                <Link to="/auth">
                  <Button size="xl" variant="hero" className="w-full sm:w-auto">
                    Try Your First Swipe
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Button size="xl" variant="outline" className="w-full sm:w-auto group">
                  <PlayCircle className="mr-2 h-4 w-4 group-hover:text-primary transition-colors" />
                  Watch 12 Seconds
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 text-sm text-muted-foreground">
                <span>No credit card. Setup in 90 seconds.</span>
                <LiveCounter />
              </div>
            </motion.div>

            {/* Hero animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-end"
            >
              <SwipeHeroAnimation />
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-muted-foreground/50 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Demo Strip Section */}
      <section className="py-20 sm:py-28 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Five swipes. Five problems solved.
            </h2>
            <ArcDivider className="mx-auto" color="primary" size="md" />
          </motion.div>

          <DemoStrip />

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center text-lg text-muted-foreground mt-12 italic"
          >
            "That was your morning. Before coffee."
          </motion.p>
        </div>
      </section>

      {/* Three Truths Section */}
      <section className="py-20 sm:py-28">
        <div className="container px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              What changes.
            </h2>
            <ArcDivider className="mx-auto" color="muted" size="md" />
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-16">
            {[
              {
                before: "SOPs stop dying in Google Docs.",
                after: "They become cards in your pocket.",
                icon: FileText,
              },
              {
                before: "Tasks stop being someone's side job.",
                after: "They become 2-second decisions.",
                icon: Clock,
              },
              {
                before: "Proof stops being 'trust me.'",
                after: "It becomes a photo timestamp.",
                icon: Camera,
              },
            ].map((truth, index) => {
              const Icon = truth.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-6">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-xl sm:text-2xl text-muted-foreground mb-2">
                    {truth.before}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-foreground">
                    {truth.after}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Agent Section */}
      <section className="py-20 sm:py-28 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <AgentIntro />
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 sm:py-28">
        <div className="container px-4 sm:px-6">
          <TestimonialSingle
            quote="We replaced a 20-person ops WhatsApp group with this. Nobody asks 'did you do it?' anymore."
            author="Priya Sharma"
            role="Head of Operations"
            company="RetailChain India"
            location="Mumbai"
          />
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 sm:py-28 bg-muted/30">
        <div className="container px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Cost less than the problem.
            </h2>
            <ArcDivider className="mx-auto" color="muted" size="md" />
          </motion.div>

          <PricingInline />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 sm:py-32 bg-gradient-to-br from-primary/10 via-primary/5 to-secondary/10">
        <div className="container px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-8">
              Your first swipe is waiting.
            </h2>
            <Link to="/auth">
              <Button size="xl" variant="hero" className="shadow-glow">
                Start Free — 90 seconds
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Fixed bottom CTA for mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-lg border-t border-border sm:hidden z-40 safe-area-bottom">
        <Link to="/auth" className="block">
          <Button size="lg" variant="hero" className="w-full">
            Try Your First Swipe
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Landing;
